// Extrait de src/components/Sidebar.js (découpe iso du 06/09/2026).
// 28/10/2026 (demande William) : la pastille ne flotte plus, elle est une icône du RAIL DROIT (RailDroit.js).
// Un clic (gauche, ou Entrée au clavier) ouvre l'historique des changements, ce que faisait le clic droit.
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Tag } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { TYPE_CONFIG } from './sidebarShared';

/**
 * Version déployée, superadmin seulement (inutile d'interroger /api/version + 2 fichiers statiques pour
 * les autres). `etat` : 'inactif' (pas superadmin), 'chargement', 'ok' (info connue) ou 'echec' (rien
 * de lisible) — le rail ne garde sa place pendant le chargement que pour ne pas faire sauter la page.
 */
export function useVersionPortail() {
  const { user } = useAuth();
  const actif = user?.role === 'superadmin';
  const [info, setInfo] = useState(null);
  const [changelog, setChangelog] = useState([]);
  const [etat, setEtat] = useState('chargement');

  useEffect(() => {
    if (!actif) return undefined;
    let vivant = true;
    const poser = (f) => (...a) => { if (vivant) f(...a); };
    setEtat('chargement');
    fetch('/api/version')
      .then(r => r.ok ? r.json() : null)
      .then(poser(data => {
        if (data?.ok && data.info) {
          setInfo(data.info);
          if (Array.isArray(data.changelog)) setChangelog(data.changelog);
          setEtat('ok');
        } else {
          setEtat('echec');
        }
      }))
      .catch(() => {
        // Repli sur les fichiers statiques
        fetch('/build-info.json?' + Date.now())
          .then(r => r.ok ? r.json() : null)
          .then(poser(d => { if (d) { setInfo(d); setEtat('ok'); } else setEtat('echec'); }))
          .catch(poser(() => setEtat('echec')));
        fetch('/changelog.json?' + Date.now())
          .then(r => r.ok ? r.json() : [])
          .then(poser(d => { if (Array.isArray(d)) setChangelog(d); }))
          .catch(() => {});
      });
    return () => { vivant = false; };
  }, [actif]);

  return { info, changelog, etat: actif ? etat : 'inactif' };
}

// Rendue par le rail droit (RailDroit.js), qui lui passe `useVersionPortail()`.
function BuildVersionBadge({ version }) {
  const { info, changelog = [] } = version || {};
  const [showPanel, setShowPanel] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [copied, setCopied] = useState(false);
  const declencheur = useRef(null);
  const fermer = useRef(null);
  const etaitOuvert = useRef(false);
  const volet = useRef(null);

  // Piège de focus : Tab / Maj+Tab bouclent dans le volet (modal) au lieu de repartir dans la page derrière.
  const pieger = (e) => {
    if (e.key !== 'Tab' || !volet.current) return;
    const cibles = Array.from(volet.current.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'));
    if (cibles.length === 0) return;
    const premier = cibles[0];
    const dernier = cibles[cibles.length - 1];
    if (e.shiftKey && document.activeElement === premier) { e.preventDefault(); dernier.focus(); }
    else if (!e.shiftKey && document.activeElement === dernier) { e.preventDefault(); premier.focus(); }
  };

  // Échap ferme l'historique (comme un clic sur le fond).
  useEffect(() => {
    if (!showPanel) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setShowPanel(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showPanel]);

  // Volet modal : le focus y entre à l'ouverture et revient à la pastille à la fermeture.
  useEffect(() => {
    if (showPanel) fermer.current?.focus();
    else if (etaitOuvert.current) declencheur.current?.focus();
    etaitOuvert.current = showPanel;
  }, [showPanel]);

  const copyDebug = () => {
    const commitShort = (info?.commit || '?').slice(0, 8);
    const latest = changelog[0];
    const lines = [
      `Probleme de deploiement — Commit actif : ${commitShort}`,
      `Build: ${info?.buildTime || 'unknown'} | ${changelog.length} changelog entries`,
      latest ? `Latest: ${latest.hash} - ${latest.type}(${latest.scope || '-'}): ${latest.message}` : '',
      `URL: ${window.location.origin}`,
    ].filter(Boolean).join('\n');
    navigator.clipboard.writeText(lines);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!info) return null;

  const commitShort = (info.commit || '?').slice(0, 8);
  const buildDate = info.buildTime && info.buildTime !== 'unknown' && info.buildTime !== 'pending'
    ? new Date(info.buildTime).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : null;

  // Filter and group changelog by date
  const filtered = changelog.filter(entry => {
    if (typeFilter && entry.type !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (entry.message || '').toLowerCase().includes(q)
        || (entry.scope || '').toLowerCase().includes(q)
        || (entry.hash || '').toLowerCase().includes(q);
    }
    return true;
  });
  const byDate = {};
  filtered.forEach(entry => {
    const day = new Date(entry.date).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
    if (!byDate[day]) byDate[day] = [];
    byDate[day].push(entry);
  });

  return (
    <>
      {/* Pastille compacte du rail : icône + début du commit ; clic = historique des changements */}
      <button
        type="button"
        ref={declencheur}
        data-testid="rail-version"
        onClick={() => setShowPanel(prev => !prev)}
        aria-haspopup="dialog"
        aria-expanded={showPanel}
        aria-label={`Version ${commitShort}${buildDate ? `, déployée le ${buildDate}` : ''} — ouvrir l'historique des changements`}
        title={`v:${commitShort}${buildDate ? ` — ${buildDate}` : ''} — cliquer pour l'historique des changements`}
        style={{
          width: 32, padding: '5px 0 4px', borderRadius: 6, cursor: 'pointer',
          background: copied ? '#166534' : '#1a2b3d',
          border: `1px solid ${copied ? 'rgba(74,222,128,0.5)' : 'rgba(91,163,230,0.25)'}`,
          color: copied ? '#4ade80' : 'rgba(255,255,255,0.75)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, fontWeight: 600, lineHeight: 1,
        }}
      >
        <Tag size={13} aria-hidden="true" />
        <span>{copied ? '✓' : commitShort.slice(0, 4)}</span>
      </button>

      {/* Historique : rendu dans <body> (portail) — dans le rail, il resterait enfermé dans son plan
          d'empilement (z 85) et passerait sous l'aide ancrée ou le chat malgré ses z-index 10000/10001. */}
      {showPanel && createPortal(
        <>
          <div onClick={() => setShowPanel(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000 }} />
          <div ref={volet} onKeyDown={pieger} role="dialog" aria-modal="true" aria-label="Historique des changements" style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, width: 420, maxWidth: '90vw',
            background: '#0f1923', zIndex: 10001, overflowY: 'auto',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.5)',
            fontFamily: "'IBM Plex Sans', sans-serif",
          }}>
            {/* Panel header */}
            <div style={{
              position: 'sticky', top: 0, background: '#0f1923',
              padding: '16px 20px', borderBottom: '1px solid rgba(91,163,230,0.15)',
              zIndex: 1,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', letterSpacing: 0.5 }}>
                    METERIS Portail
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {commitShort} {buildDate ? `| deploy ${buildDate}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    onClick={copyDebug}
                    title="Copier le debug dans le presse-papiers"
                    style={{
                      background: copied ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.08)',
                      border: 'none', color: copied ? '#4ade80' : 'rgba(255,255,255,0.6)',
                      fontSize: 11, cursor: 'pointer', borderRadius: 6, padding: '0 10px', height: 32,
                      fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: 600,
                    }}
                  >{copied ? '✓ Copié' : 'Copier debug'}</button>
                  <button ref={fermer} onClick={() => setShowPanel(false)} aria-label="Fermer l'historique" style={{
                    background: 'rgba(255,255,255,0.08)', border: 'none', color: 'rgba(255,255,255,0.5)',
                    fontSize: 18, cursor: 'pointer', borderRadius: 6, width: 32, height: 32,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>x</button>
                </div>
              </div>

              {/* Search */}
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher dans l'historique..."
                style={{
                  width: '100%', marginTop: 10, padding: '7px 10px',
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 6, color: '#fff', fontSize: 12, boxSizing: 'border-box',
                  fontFamily: "'IBM Plex Sans', sans-serif", outline: 'none',
                }}
              />

              {/* Type filter pills */}
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                <button onClick={() => setTypeFilter('')} style={{
                  fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 3, border: 'none', cursor: 'pointer',
                  background: !typeFilter ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.06)',
                  color: !typeFilter ? '#fff' : 'rgba(255,255,255,0.4)', letterSpacing: 0.3,
                }}>Tous ({changelog.length})</button>
                {Object.entries(TYPE_CONFIG).filter(([k]) => k !== 'other').map(([key, cfg]) => {
                  const count = changelog.filter(e => e.type === key).length;
                  if (count === 0) return null;
                  return (
                    <button key={key} onClick={() => setTypeFilter(typeFilter === key ? '' : key)} style={{
                      fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 3, border: 'none', cursor: 'pointer',
                      background: typeFilter === key ? cfg.bg : 'rgba(255,255,255,0.06)',
                      color: typeFilter === key ? cfg.color : 'rgba(255,255,255,0.4)', letterSpacing: 0.3,
                    }}>{cfg.label} ({count})</button>
                  );
                })}
              </div>
            </div>

            {/* Entries grouped by date */}
            <div style={{ padding: '8px 0' }}>
              {Object.entries(byDate).map(([day, entries]) => (
                <div key={day}>
                  <div style={{
                    padding: '10px 20px 4px', fontSize: 11, fontWeight: 700,
                    color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: 0.8,
                    position: 'sticky', top: 90, background: '#0f1923', zIndex: 1,
                  }}>{day}</div>
                  {entries.map(entry => {
                    const tc = TYPE_CONFIG[entry.type] || TYPE_CONFIG.other;
                    return (
                      <div key={entry.hash} style={{
                        padding: '8px 20px', display: 'flex', gap: 10, alignItems: 'flex-start',
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                      }}>
                        <span style={{
                          fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
                          background: tc.bg, color: tc.color, flexShrink: 0, marginTop: 2,
                          minWidth: 65, textAlign: 'center',
                        }}>{tc.label}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: '#fff', lineHeight: 1.4 }}>
                            {entry.message}
                          </div>
                          <div style={{ display: 'flex', gap: 8, marginTop: 3, alignItems: 'center' }}>
                            <span style={{
                              fontSize: 10, fontFamily: "'IBM Plex Mono', monospace",
                              color: 'rgba(91,163,230,0.6)',
                            }}>{entry.hash}</span>
                            {entry.scope && (
                              <span style={{
                                fontSize: 9, padding: '1px 5px', borderRadius: 3,
                                background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)',
                              }}>{entry.scope}</span>
                            )}
                            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', fontFamily: "'IBM Plex Mono', monospace" }}>
                              {new Date(entry.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
              {filtered.length === 0 && (
                <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>
                  Aucun historique disponible
                </div>
              )}
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

export default BuildVersionBadge;
